import { useState } from 'react'
import { apiClient } from '@/shared/api/apiClient'
import type { ManualStandardRequirementItemWithValidation } from '@/shared/hooks/api/machineryDirectiveCompliance'

/** First progress line: "Validating 12 statement(s)..." */
const PROGRESS_TOTAL_ONLY_REGEX = /Validating (\d+) statement/
/** Per-statement line: "Validating statement 3 of 12..." */
const PROGRESS_INDEX_REGEX = /Validating statement (\d+) of (\d+)/

type LegacyStreamEvent =
  | { type: 'validation_start'; totalStatements: number }
  | {
      type: 'statement_start'
      id: string
      number: string
      text: string
      statementIndex: number
      totalStatements: number
    }
  | {
      type: 'statement_result'
      id: string
      number: string
      text: string
      verified: boolean
      explanation: string
      statementIndex: number
      totalStatements: number
    }
  | { type: 'complete'; standard: string; totalStatements: number }
  | { type: 'error'; error: string }

function parseWireEvent(raw: unknown): LegacyStreamEvent | null {
  if (!raw || typeof raw !== 'object' || !('type' in raw)) {
    return null
  }
  const eventRecord = raw as Record<string, unknown>
  const eventType = eventRecord.type

  if (typeof eventType !== 'string') {
    return null
  }

  switch (eventType) {
    case 'progress': {
      const message =
        typeof eventRecord.message === 'string' ? eventRecord.message : ''
      const totalMatch = message.match(PROGRESS_TOTAL_ONLY_REGEX)
      if (totalMatch) {
        const totalStatements = Number(totalMatch[1])
        return {
          type: 'validation_start',
          totalStatements,
        }
      }
      const indexMatch = message.match(PROGRESS_INDEX_REGEX)
      if (
        indexMatch &&
        typeof indexMatch[1] === 'string' &&
        typeof indexMatch[2] === 'string'
      ) {
        const statementIndex = Number(indexMatch[1])
        const totalStatements = Number(indexMatch[2])
        return {
          type: 'statement_start',
          id: '',
          number: '',
          text: '',
          statementIndex,
          totalStatements,
        }
      }
      return null
    }
    case 'llm.started': {
      if (eventRecord.step !== 'validate_statements') {
        return null
      }
      const stepId = eventRecord.stepId
      if (typeof stepId !== 'string' || stepId.length === 0) {
        return null
      }
      return {
        type: 'statement_start',
        id: stepId,
        number: '',
        text: '',
        statementIndex: 0,
        totalStatements: 0,
      }
    }
    case 'step.completed': {
      if (eventRecord.step !== 'validate_statements') {
        return null
      }
      const resultRaw = eventRecord.result
      if (!resultRaw || typeof resultRaw !== 'object') {
        return null
      }
      const result = resultRaw as Record<string, unknown>
      if (
        typeof result.id !== 'string' ||
        typeof result.verified !== 'boolean' ||
        typeof result.explanation !== 'string'
      ) {
        return null
      }
      const number =
        typeof result.number === 'string' ? result.number : undefined
      const text = typeof result.text === 'string' ? result.text : ''
      return {
        type: 'statement_result',
        id: result.id,
        number: number ?? '',
        text,
        verified: result.verified,
        explanation: result.explanation,
        statementIndex: 0,
        totalStatements: 0,
      }
    }
    case 'workflow.completed': {
      return { type: 'complete', standard: '', totalStatements: 0 }
    }
    case 'workflow.failed': {
      const err =
        typeof eventRecord.error === 'string'
          ? eventRecord.error
          : 'Validation failed'
      return { type: 'error', error: err }
    }
    case 'error': {
      if (typeof eventRecord.error !== 'string') {
        return null
      }
      return { type: 'error', error: eventRecord.error }
    }
    case 'validation_start':
    case 'statement_start':
    case 'statement_result':
    case 'complete':
      return eventRecord as LegacyStreamEvent
    default:
      return null
  }
}

export function useValidateStandardStatements(
  complianceId: string,
  standardId: string,
  documentId?: string
) {
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [validationProgress, setValidationProgress] = useState<{
    current: number
    total: number
  } | null>(null)
  const [validationItems, setValidationItems] = useState<
    Map<string, ManualStandardRequirementItemWithValidation>
  >(new Map())

  const validate = async () => {
    setIsValidating(true)
    setError(null)
    setProcessingId(null)
    setValidationProgress(null)
    setValidationItems(new Map())

    try {
      const baseURL = apiClient.defaults.baseURL ?? ''
      const url = `${baseURL}/machinery-directive-compliance/${complianceId}/standard/${standardId}/validate-statements`

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          'Accept-Language': navigator.language || 'en',
        },
        body: JSON.stringify(
          documentId !== undefined ? { documentId } : {}
        ),
      })

      if (!response.ok) {
        throw new Error(`Failed to start validation: ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('No response body reader available')
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const raw = JSON.parse(line.slice(6)) as unknown
              const eventData = parseWireEvent(raw)
              if (eventData === null) {
                continue
              }

              switch (eventData.type) {
                case 'validation_start':
                  setValidationProgress({
                    current: 0,
                    total: eventData.totalStatements,
                  })
                  break

                case 'statement_start': {
                  if (eventData.id.length > 0) {
                    setProcessingId(eventData.id)
                  }
                  if (eventData.totalStatements > 0) {
                    setValidationProgress({
                      current: eventData.statementIndex - 1,
                      total: eventData.totalStatements,
                    })
                  }
                  break
                }

                case 'statement_result': {
                  setValidationItems((prev) => {
                    const next = new Map(prev)
                    const entry: ManualStandardRequirementItemWithValidation = {
                      id: eventData.id,
                      text: eventData.text,
                      verified: eventData.verified,
                      explanation: eventData.explanation,
                    }
                    if (eventData.number.length > 0) {
                      entry.number = eventData.number
                    }
                    next.set(eventData.id, entry)
                    return next
                  })
                  setProcessingId(null)
                  setValidationProgress((prev) => {
                    const total =
                      eventData.totalStatements > 0
                        ? eventData.totalStatements
                        : (prev?.total ?? 0)
                    const completedByIndex =
                      eventData.statementIndex > 0
                        ? eventData.statementIndex
                        : null
                    const nextCurrent =
                      completedByIndex != null
                        ? completedByIndex
                        : (prev?.current ?? 0) + 1
                    if (total <= 0) {
                      return prev
                    }
                    return { current: nextCurrent, total }
                  })
                  break
                }

                case 'complete':
                  setIsValidating(false)
                  setProcessingId(null)
                  setValidationProgress(null)
                  break

                case 'error':
                  setError(eventData.error)
                  setIsValidating(false)
                  setProcessingId(null)
                  setValidationProgress(null)
                  break
              }
            } catch {
              // Ignore malformed SSE lines
            }
          }
        }
      }

      setIsValidating(false)
      setProcessingId(null)
      setValidationProgress(null)
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to validate standard statements'
      setError(message)
      setIsValidating(false)
      setProcessingId(null)
      setValidationProgress(null)
    }
  }

  return {
    isValidating,
    error,
    processingId,
    validationProgress,
    validationItems,
    validate,
  }
}
